import React from 'react';
import { styled } from '@compiled/react';
import Badge, { type BadgeProps } from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import { Box, Text, xcss } from '@atlaskit/primitives';
import type {
	AllowedOperators,
	OnChangeOperator,
	OperatorOption,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { badgeTestId, labelContainerTestId } from '../constants.tsx';
import DropdownOperator from '../dropdown-operator/index.tsx';
import { useVerticalMode } from '../../../../controllers/feature-context.tsx';
import { PickerTriggerLayout } from '../../picker-trigger-layout/index.tsx';

const hasValue = (value: SelectValue): boolean =>
	Array.isArray(value) ? value.length > 0 : typeof value === 'object' && value !== null;

type Params = {
	isInvalid?: boolean;
	label?: string | null;
	value?: SelectValue;
	allowedOperators: AllowedOperators;
	onChangeOperator?: OnChangeOperator;
	selectedOperator?: OperatorOption;
	isParentPickerOpen: boolean;
	onOpenOperatorPicker: () => void;
	isSelected?: boolean;
};

const TriggerComponent = ({
	isInvalid = false,
	label = '',
	value,
	allowedOperators,
	onChangeOperator,
	selectedOperator,
	isParentPickerOpen,
	onOpenOperatorPicker,
	isSelected,
}: Params): React.JSX.Element => {
	const verticalMode = useVerticalMode();

	if (!value || !hasValue(value)) {
		return isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons') ? (
			<Box xcss={triggerContainerStyles}>
				<Box xcss={triggerStyles} testId={labelContainerTestId}>
					<Text weight="medium" color={!isSelected ? 'color.text.subtle' : 'inherit'}>
						{label}
					</Text>
				</Box>
			</Box>
		) : (
			<Container>
				<LabelContainer data-testid={labelContainerTestId}>{label}</LabelContainer>
			</Container>
		);
	}

	const valueMap = value.map((v) =>
		v.optionType !== 'error' && v.label !== undefined ? v.label : '',
	);
	const valueLength = valueMap.length;
	const [firstValue, ...rest] = valueMap;

	const badgeAppearance: BadgeProps['appearance'] = isInvalid ? 'important' : 'primary';

	const pickerValue = (
		<>
			<DropdownOperator
				isInvalid={isInvalid}
				allowedOperators={allowedOperators}
				selectedOperator={selectedOperator}
				onChangeOperator={onChangeOperator}
				isParentPickerOpen={isParentPickerOpen}
				onOpenOperatorPicker={onOpenOperatorPicker}
			/>
			<ValueContainer>{firstValue}</ValueContainer>
			{valueLength > 1 ? (
				<StyledBadge>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Badge testId={badgeTestId} appearance={badgeAppearance}>{`+${rest.length}`}</Badge>
				</StyledBadge>
			) : null}
		</>
	);

	if (verticalMode) {
		return (
			<PickerTriggerLayout
				label={label || ''}
				ariaLabel={`${selectedOperator?.ariaLabel} ${firstValue}`}
			>
				{pickerValue}
			</PickerTriggerLayout>
		);
	}

	return (
		<Container aria-label={`${selectedOperator?.ariaLabel} ${firstValue}`}>
			<LabelContainer data-testid={labelContainerTestId}>
				<StyledLabel>{label}</StyledLabel>
			</LabelContainer>
			{pickerValue}
		</Container>
	);
};

const triggerContainerStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
});

const triggerStyles = xcss({
	maxWidth: '192px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.span({
	display: 'inline-flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelContainer = styled.span({
	maxWidth: '192px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueContainer = styled.span({
	maxWidth: '192px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLabel = styled.span({
	fontWeight: token('font.weight.bold'),
	marginRight: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledBadge = styled.span({
	display: 'inline-flex',
	alignItems: 'center',
	marginLeft: token('space.050'),
});

export default TriggerComponent;
